import { useEffect, useState } from "react";
import { Download, Search, StickyNote } from "lucide-react";
import api, { authHeaders, downloadCsv } from "@/lib/api";
import { LEAD_STATUSES, LEAD_STATUS_LABELS, LEAD_TYPE_LABELS } from "@/lib/constants";

function LeadRow({ lead, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  const patch = (body) => {
    api.patch(`/admin/leads/${lead._id}`, body, { headers: authHeaders() }).then(({ data }) => onUpdate(data));
  };

  const addNote = () => {
    if (!note.trim()) return;
    patch({ note: note.trim() });
    setNote("");
  };

  const date = new Date(lead.created_at).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className={`border border-white/10 bg-ink-900 ${lead.request_type === "URGENT" ? "border-l-4 border-l-red-500" : ""}`} data-testid={`admin-lead-${lead._id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left" data-testid={`admin-lead-toggle-${lead._id}`}>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
          <span className="font-display text-lg font-bold uppercase tracking-wide text-paper">{lead.name}</span>
          <span className="font-mono text-xs text-copper">{lead.phone}</span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{lead.work_type}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] text-muted-foreground">{date}</span>
          <span
            className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${
              lead.status === "NEW" ? "bg-copper/15 text-copper" : lead.status === "LOST" ? "bg-red-500/15 text-red-400" : "bg-white/10 text-paper/70"
            }`}
            data-testid={`admin-lead-status-${lead._id}`}
          >
            {LEAD_STATUS_LABELS[lead.status] || lead.status}
          </span>
          <span
            className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${
              lead.request_type === "URGENT" ? "animate-pulse bg-red-500 text-white" : "border border-copper/40 text-copper"
            }`}
            data-testid={`admin-lead-type-${lead._id}`}
          >
            {LEAD_TYPE_LABELS[lead.request_type] || "Proiect"}
          </span>
        </div>
      </button>

      {open && (
        <div className="space-y-5 border-t border-white/10 px-5 py-5" data-testid={`admin-lead-detail-${lead._id}`}>
          <div className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
            <p><span className="label-brox !mb-1">Locație</span>{lead.location}</p>
            <p><span className="label-brox !mb-1">Suprafață</span>{lead.area || "—"}</p>
            <p><span className="label-brox !mb-1">Materiale</span>{lead.materials_option || lead.materials || "—"}{lead.materials_needed && <span className="ml-2 border border-copper/40 px-1.5 py-0.5 font-mono text-[10px] text-copper">MANOPERĂ + MATERIALE</span>}</p>
            <p><span className="label-brox !mb-1">Tip cerere</span>{LEAD_TYPE_LABELS[lead.request_type] || "Proiect"}</p>
            <p><span className="label-brox !mb-1">Servicii solicitate</span>{(lead.services || []).join(", ") || lead.work_type}</p>
            <p><span className="label-brox !mb-1">Start dorit</span>{lead.timeline || "—"}</p>
            <p><span className="label-brox !mb-1">Sursă</span>{lead.source}</p>
            <p><span className="label-brox !mb-1">Telefon client</span><a href={`tel:${lead.phone}`} className="text-copper">{lead.phone}</a></p>
          </div>
          {lead.description && (
            <div>
              <p className="label-brox">Descriere proiect</p>
              <p className="border border-white/10 bg-ink-950 p-4 text-sm leading-relaxed text-paper/85">{lead.description}</p>
            </div>
          )}

          <div>
            <label className="label-brox">Status lead</label>
            <select
              value={lead.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="input-brox max-w-xs"
              data-testid={`admin-lead-status-select-${lead._id}`}
            >
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="label-brox">Note interne</p>
            <div className="space-y-2">
              {(lead.notes || []).map((n, i) => (
                <p key={i} className="flex items-start gap-2 border border-white/10 bg-ink-950 px-4 py-3 text-sm text-paper/80" data-testid={`admin-lead-note-${lead._id}-${i}`}>
                  <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-copper" />
                  <span>{n.text}<span className="ml-2 font-mono text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}</span></span>
                </p>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                className="input-brox flex-1"
                placeholder="Adaugă notă internă..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid={`admin-lead-note-input-${lead._id}`}
              />
              <button onClick={addNote} className="btn-primary !px-5 !py-2 !min-h-[48px] text-base" data-testid={`admin-lead-note-add-${lead._id}`}>ADAUGĂ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  const load = (query = "", st = "", tp = "") => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (st) params.set("status", st);
    if (tp) params.set("type", tp);
    api.get(`/admin/leads?${params.toString()}`, { headers: authHeaders() }).then(({ data }) => setLeads(data || []));
  };

  useEffect(() => {
    load();
  }, []);

  const onUpdate = (updated) => {
    setLeads((prev) => prev.map((l) => (l._id === updated._id ? updated : l)));
  };

  return (
    <div data-testid="admin-leads-tab">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input-brox !pl-11"
            placeholder="Caută după nume, telefon, locație..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, status)}
            data-testid="admin-leads-search"
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); load(q, e.target.value, type); }} className="input-brox sm:max-w-xs" data-testid="admin-leads-status-filter">
          <option value="">Toate statusurile</option>
          {LEAD_STATUSES.map((s) => (
            <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>
          ))}
        </select>
        <select value={type} onChange={(e) => { setType(e.target.value); load(q, status, e.target.value); }} className="input-brox sm:max-w-xs" data-testid="admin-leads-type-filter">
          <option value="">Toate tipurile</option>
          <option value="PROJECT">Proiect / lucrare</option>
          <option value="SMALL_JOB">Lucrare mică / reparație</option>
          <option value="URGENT">Urgent</option>
          <option value="B2B_MAINTENANCE">B2B mentenanță</option>
        </select>
        <button
          onClick={() => downloadCsv("/admin/leads/export", "brox-leads.csv")}
          className="inline-flex min-h-[52px] shrink-0 items-center justify-center gap-2 border border-white/15 px-5 font-display text-base font-bold uppercase tracking-wider text-paper transition-colors hover:border-copper hover:text-copper"
          data-testid="admin-leads-export-btn"
        >
          <Download className="h-4 w-4" />
          EXPORTĂ CSV
        </button>
      </div>

      {leads === null && <p className="text-sm text-muted-foreground">Se încarcă...</p>}
      {leads !== null && leads.length === 0 && (
        <p className="border border-dashed border-white/15 p-10 text-center text-sm text-muted-foreground" data-testid="admin-leads-empty">
          Nicio cerere de ofertă încă. Lead-urile apar aici imediat ce clienții trimit formularul.
        </p>
      )}
      <div className="space-y-3">
        {leads?.map((lead) => <LeadRow key={lead._id} lead={lead} onUpdate={onUpdate} />)}
      </div>
    </div>
  );
}
